/**
 * @file interpret/components/InterpretMicButton.tsx
 * @brief The phone's interpreter mic (`MicButton` via `CircularProgressMic`), on the web.
 *
 * @details
 * DES, 01-10-2026: the controls must be the phone's interpretation screen's, not
 * the Boardroom recorder's. Port of FRONTEND `components/MicButton.tsx` as the
 * Silero screen uses it (`micSizeOverride={168}`):
 * - 168px disc, #2A2A2A in light; in dark the page colour with a 1.5px ring.
 * - live: a brand dot, and a brand halo (1.12× the disc) that swells up to +38%
 *   with the mic level, opacity 0.06 → 0.32 (0.38 dark); read per frame from a
 *   ref, never React state.
 * - busy (starting, translating, ending): two pulsing white dots and a spinning
 *   brand arc (3.5px, 30% of the ring, 1.1 s a turn).
 * - paused: the solid white Play at 62% of the icon box (mic glyph standard).
 * - playing: the disc fades out (opacity 0, scale 0.86) and the translation's
 *   caption takes its place, word by word with the audio, as the phone's
 *   `PlaybackCaption` (ported 02-10-2026: was the whole caption at once). The
 *   disc comes back once the caption has floated away.
 * - tap and hold: `onTap`, and `onLongPress` after 800 ms (`delayLongPress`).
 *   A hold never also fires the tap.
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { MicOff, Play } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { PlaybackCaption, type CaptionProgress } from "./PlaybackCaption";

export type InterpretMicState = "live" | "busy" | "paused" | "disabled";

const SIZE = 168;
/** The phone's slot around the disc (×1.28) and the caption's box. */
const SLOT = Math.round(SIZE * 1.28);
const ICON = SIZE * 0.44;
const LONG_PRESS_MS = 800;
const GLOW_SCALE_BOOST = 0.38;
/**
 * (05-10-2026, DES: "timer is too close to the circular glow") The halo
 * (SIZE × 1.12) grows to × (1 + GLOW_SCALE_BOOST) ≈ 260px, past the 215px
 * SLOT, so it reached the timer above and the status below. Room above and
 * below for the full glow plus an 8px gap; SLOT stays the caption's box.
 */
const GLOW_ROOM = Math.ceil((SIZE * 1.12 * (1 + GLOW_SCALE_BOOST) - SLOT) / 2) + 8;
const ATTACK = 0.72;
const RELEASE = 0.28;

type Props = {
  state: InterpretMicState;
  /** 0..100, read every frame for the halo. */
  levelRef: { current: number };
  /** The translation being played: replaces the disc while set. */
  caption: { text: string; lang: string | null; style?: CSSProperties } | null;
  /** Where the playing translation is, for the word-by-word caption. */
  getProgress: () => CaptionProgress;
  onTap: () => void;
  onLongPress: () => void;
  ariaLabel: string;
  /** Paused glyph: the solid Play (default) or mic-off, when another control
   *  (the Interpreter dock, 09-10-2026) is the Resume action. */
  pausedGlyph?: "play" | "micOff";
};

export function InterpretMicButton({
  state,
  levelRef,
  caption,
  getProgress,
  onTap,
  onLongPress,
  ariaLabel,
  pausedGlyph = "play",
}: Props) {
  const haloRef = useRef<HTMLDivElement>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldRef = useRef(false);
  // The caption outlives the audio by its hold and float; the disc waits.
  const [captionUp, setCaptionUp] = useState(false);
  const covered = !!caption || captionUp;
  const live = state === "live" && !covered;

  // Halo follows the mic, as MicButton's `meterReactiveGlow`.
  useEffect(() => {
    const halo = haloRef.current;
    if (!halo) return;
    if (!live) {
      halo.style.opacity = "0";
      halo.style.transform = "scale(1)";
      return;
    }
    const dark = document.documentElement.classList.contains("dark");
    const peak = dark ? 0.38 : 0.32;
    let smooth = 0;
    let raf = 0;
    const tick = () => {
      const raw = Math.max(0, Math.min(1, (levelRef.current ?? 0) / 100));
      smooth += (raw - smooth) * (raw > smooth ? ATTACK : RELEASE);
      const eased = Math.pow(smooth, 0.65);
      halo.style.opacity = smooth < 0.008 ? "0" : String(0.06 + eased * peak);
      halo.style.transform = `scale(${1 + eased * GLOW_SCALE_BOOST})`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [live, levelRef]);

  const clearHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: SLOT, height: SLOT, marginBlock: GLOW_ROOM }}
    >
      <div
        className={cn(
          "absolute inset-0 flex items-center justify-center transition-[opacity,transform] duration-300 ease-out",
          covered ? "pointer-events-none scale-[0.86] opacity-0" : "scale-100 opacity-100",
        )}
      >
        <div
          ref={haloRef}
          aria-hidden
          className="pointer-events-none absolute rounded-full bg-brand"
          style={{
            width: SIZE * 1.12,
            height: SIZE * 1.12,
            opacity: 0,
            willChange: "transform, opacity",
          }}
        />
        <button
          type="button"
          aria-label={ariaLabel}
          disabled={state === "disabled"}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            heldRef.current = false;
            clearHold();
            holdTimer.current = setTimeout(() => {
              heldRef.current = true;
              onLongPress();
            }, LONG_PRESS_MS);
          }}
          onPointerUp={clearHold}
          onPointerLeave={clearHold}
          onPointerCancel={clearHold}
          onContextMenu={(e) => e.preventDefault()}
          onClick={() => {
            if (heldRef.current) {
              heldRef.current = false;
              return;
            }
            onTap();
          }}
          className={cn(
            "relative flex select-none items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4",
            "bg-[#2A2A2A] dark:bg-background dark:ring-[1.5px] dark:ring-inset dark:ring-border",
            state === "disabled" && "cursor-not-allowed bg-[#555555] opacity-60",
          )}
          style={{ width: SIZE, height: SIZE, touchAction: "manipulation" }}
        >
          {state === "busy" ? (
            <>
              <svg
                aria-hidden
                className="absolute inset-0 animate-spin"
                style={{ animationDuration: "1.1s" }}
                viewBox={`0 0 ${SIZE} ${SIZE}`}
              >
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={SIZE / 2 - 1.75}
                  fill="none"
                  stroke="hsl(var(--brand))"
                  strokeWidth={3.5}
                  strokeLinecap="round"
                  strokeDasharray={`${Math.PI * (SIZE - 3.5) * 0.3} ${Math.PI * (SIZE - 3.5)}`}
                />
              </svg>
              <span className="flex gap-2" aria-hidden>
                <span className="h-3 w-3 animate-pulse rounded-full bg-white" />
                <span className="h-3 w-3 animate-pulse rounded-full bg-white [animation-delay:300ms]" />
              </span>
            </>
          ) : state === "paused" && pausedGlyph === "micOff" ? (
            <MicOff
              aria-hidden
              className="text-white"
              strokeWidth={1.5}
              style={{ width: ICON * 0.72, height: ICON * 0.72 }}
            />
          ) : state === "paused" ? (
            <Play
              aria-hidden
              className="ml-1.5 text-white"
              fill="#fff"
              strokeLinejoin="round"
              style={{ width: ICON * 0.62, height: ICON * 0.62 }}
            />
          ) : state === "disabled" ? (
            <MicOff
              aria-hidden
              className="text-white"
              strokeWidth={1.5}
              style={{ width: ICON * 0.72, height: ICON * 0.72 }}
            />
          ) : (
            <span
              aria-hidden
              className="rounded-full bg-brand"
              style={{ width: Math.max(18, SIZE * 0.2), height: Math.max(18, SIZE * 0.2) }}
            />
          )}
        </button>
      </div>

      <PlaybackCaption
        text={caption?.text ?? ""}
        lang={caption?.lang ?? null}
        style={caption?.style}
        getProgress={getProgress}
        onVisibleChange={setCaptionUp}
      />
    </div>
  );
}
